<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import botFlowsAPI from 'dashboard/api/botFlows';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { createReteFlowBuilder } from './reteFlowBuilder';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();
const editorContainerRef = ref(null);
const editorStageRef = ref(null);
const flowBuilder = ref(null);
const selectedNode = ref(null);
const contextMenu = ref(null);
const quickEdit = ref(null);
const draftConnection = ref(null);
const isSaving = ref(false);
const isLoading = ref(false);
const flowId = ref(route.params.id || null);
const flowName = ref('');
const selectedInboxId = ref('');
const currentStatus = ref('draft');
const isSyncingTemplates = ref(false);
const templateParserRef = ref(null);
const publishedFlows = ref([]);
const MAX_INTERACTIVE_OPTION_LENGTH = 20;

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabels');
const teams = useMapGetter('teams/getTeams');
const agents = useMapGetter('agents/getAgents');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const toolbarNodes = [
  { type: 'text', icon: 'i-lucide-pilcrow', label: 'Text' },
  { type: 'image', icon: 'i-lucide-image', label: 'Image' },
  { type: 'audio', icon: 'i-lucide-mic', label: 'Audio' },
  { type: 'video', icon: 'i-lucide-youtube', label: 'Video' },
  { type: 'file', icon: 'i-lucide-paperclip', label: 'File' },
  { type: 'location', icon: 'i-lucide-map-pinned', label: 'Location' },
  { type: 'template', icon: 'i-lucide-clipboard-plus', label: 'Template' },
  {
    type: 'interactive',
    icon: 'i-lucide-newspaper',
    label: 'Interactive',
    active: true,
  },
  {
    type: 'condition',
    icon: 'i-lucide-git-compare-arrows',
    label: 'Condition',
  },
  { type: 'http', icon: 'i-lucide-plug-zap', label: 'HTTP API' },
  { type: 'sheet', icon: 'i-lucide-file-spreadsheet', label: 'Google Sheet' },
  { type: 'input', icon: 'i-lucide-hand', label: 'User Input' },
];

const triggerModeOptions = computed(() => [
  { value: 'no_match', label: t('FLOW_BUILDER.INSPECTOR.NO_MATCH') },
  { value: 'keyword', label: t('FLOW_BUILDER.INSPECTOR.KEYWORD_TRIGGER') },
]);
const triggerMatchTypeOptions = computed(() => [
  { value: 'exact', label: t('FLOW_BUILDER.INSPECTOR.EXACT_MATCH') },
  { value: 'contains', label: t('FLOW_BUILDER.INSPECTOR.CONTAINS_MATCH') },
]);

const interactiveHeaderTypeOptions = computed(() => [
  { value: 'none', label: t('FLOW_BUILDER.INSPECTOR.HEADER_NONE') },
  { value: 'text', label: t('FLOW_BUILDER.INSPECTOR.HEADER_TEXT') },
]);
const buttonGenerationMethodOptions = computed(() => [
  {
    value: 'static',
    label: t('FLOW_BUILDER.INSPECTOR.BUTTON_GENERATION_STATIC'),
  },
]);
const buttonActionOptions = computed(() => [
  {
    value: 'start_flow',
    label: t('FLOW_BUILDER.INSPECTOR.BUTTON_ACTION_START_FLOW'),
  },
  {
    value: 'send_message',
    label: t('FLOW_BUILDER.INSPECTOR.BUTTON_ACTION_SEND_MESSAGE'),
  },
]);

const whatsappInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === INBOX_TYPES.WHATSAPP)
);
const whatsappInboxOptions = computed(() =>
  whatsappInboxes.value.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  }))
);
const labelOptions = computed(() =>
  (labels.value || []).map(label => ({
    value: String(label.id),
    label: label.title,
  }))
);
const teamOptions = computed(() =>
  (teams.value || []).map(team => ({
    value: String(team.id),
    label: team.name,
  }))
);
const agentOptions = computed(() =>
  (agents.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  }))
);
const publishedFlowOptions = computed(() =>
  (publishedFlows.value || []).map(flow => ({
    value: String(flow.id),
    label: flow.name,
  }))
);
const rowGenerationMethodOptions = computed(() => [
  {
    value: 'static',
    label: t('FLOW_BUILDER.INSPECTOR.ROW_GENERATION_STATIC'),
  },
]);

const selectedNodeType = computed(() => selectedNode.value?.data?.type || '');
const pageTitle = computed(() =>
  flowId.value
    ? t('FLOW_BUILDER.EDITOR_TITLE_EDIT')
    : t('FLOW_BUILDER.EDITOR_TITLE_CREATE')
);
const contextMenuItems = computed(() => [
  {
    label: t('FLOW_BUILDER.DELETE'),
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash-2',
    disabled: contextMenu.value?.node?.data?.type === 'start',
  },
]);
const quickEditNodeType = computed(
  () => quickEdit.value?.node?.data?.type || ''
);
const quickEditNode = computed(() => quickEdit.value?.node || null);
const selectedInboxTemplates = computed(() => {
  if (!selectedInboxId.value) return [];

  return getFilteredWhatsAppTemplates.value(selectedInboxId.value);
});
const selectedTemplateDefinition = computed(() => {
  if (selectedNodeType.value !== 'template') return null;

  return (
    selectedInboxTemplates.value.find(
      template =>
        String(template.id) === String(selectedNode.value?.data?.templateId)
    ) || null
  );
});
const templateOptions = computed(() =>
  selectedInboxTemplates.value.map(template => ({
    value: String(template.id),
    label: `${template.name} (${template.language || 'en'})`,
    template,
  }))
);

const getApiErrorMessage = error => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (Array.isArray(responseData?.errors) && responseData.errors.length) {
    return responseData.errors.join(', ');
  }

  if (responseData?.errors && typeof responseData.errors === 'object') {
    return Object.values(responseData.errors).flat().filter(Boolean).join(', ');
  }

  return '';
};

const exceedsInteractiveOptionLength = value =>
  String(value || '').trim().length > MAX_INTERACTIVE_OPTION_LENGTH;

const getNodeValidationErrors = node => {
  if (!node) return [];

  if (
    node.type === 'button' &&
    exceedsInteractiveOptionLength(node.data?.buttonTitle)
  ) {
    return ['button_title_too_long'];
  }

  if (
    node.type === 'row' &&
    exceedsInteractiveOptionLength(node.data?.rowTitle)
  ) {
    return ['row_title_too_long'];
  }

  return [];
};

const getFlowValidationErrors = payload => {
  const nodes = payload?.nodes || [];

  return nodes.flatMap(node =>
    getNodeValidationErrors(node).map(error => ({ nodeId: node.id, error }))
  );
};

const getOptionValue = valueOrEvent =>
  valueOrEvent?.target?.value ?? valueOrEvent;

const normalizeDelayValue = value => {
  const parsedValue = Number.parseInt(value, 10);

  return Number.isNaN(parsedValue) || parsedValue < 0 ? 0 : parsedValue;
};

const buildDraftConnectionPath = connection => {
  if (!connection) return '';

  const deltaX = Math.max(
    80,
    Math.abs(connection.end.x - connection.start.x) / 2
  );

  return `M ${connection.start.x} ${connection.start.y} C ${connection.start.x + deltaX} ${connection.start.y}, ${connection.end.x - deltaX} ${connection.end.y}, ${connection.end.x} ${connection.end.y}`;
};

const handleCanvasClick = () => {
  contextMenu.value = null;
};

const updateContextMenu = payload => {
  contextMenu.value = payload?.node ? { ...payload } : null;
};

const getEditorStageRect = () =>
  editorStageRef.value?.getBoundingClientRect() || {
    left: 0,
    top: 0,
  };

const getPointFromClientPosition = (clientX, clientY) => {
  const rect = getEditorStageRect();

  return {
    x: clientX - rect.left,
    y: clientY - rect.top,
  };
};

const getSocketCenter = socketElement => {
  const rect = socketElement.getBoundingClientRect();

  return getPointFromClientPosition(
    rect.left + rect.width / 2,
    rect.top + rect.height / 2
  );
};

const getNodeElement = nodeId =>
  editorStageRef.value?.querySelector(`.flow-node[data-node-id="${nodeId}"]`);

const syncQuickEditPosition = () => {
  if (!quickEdit.value?.node?.id) return;

  const nodeElement = getNodeElement(quickEdit.value.node.id);

  if (!nodeElement) return;

  const stageRect = getEditorStageRect();
  const nodeRect = nodeElement.getBoundingClientRect();

  quickEdit.value = {
    ...quickEdit.value,
    x: Math.min(
      nodeRect.right - stageRect.left + 16,
      Math.max(24, stageRect.width - 360)
    ),
    y: Math.max(24, nodeRect.top - stageRect.top),
  };
};

const openQuickEdit = node => {
  if (!node) return;

  quickEdit.value = {
    node,
    x: 32,
    y: 32,
  };

  syncQuickEditPosition();
};

const closeQuickEdit = () => {
  quickEdit.value = null;
};

const buildPayload = status => ({
  name: flowName.value,
  inbox_id: selectedInboxId.value,
  status,
  channel_type: 'whatsapp',
  flow_json: flowBuilder.value?.serialize() || {},
});

const loadFlow = async id => {
  isLoading.value = true;

  try {
    const response = await botFlowsAPI.show(id);
    const flow = response.data;

    flowId.value = flow.id;
    flowName.value = flow.name;
    selectedInboxId.value = String(flow.inbox_id);
    currentStatus.value = flow.status;
    await flowBuilder.value.load(flow.flow_json);
  } catch (error) {
    useAlert(getApiErrorMessage(error) || t('FLOW_BUILDER.LOAD_ERROR'));
    router.push({ name: 'flow_builder_index' });
  } finally {
    isLoading.value = false;
  }
};

const fetchPublishedFlows = async () => {
  try {
    const response = await botFlowsAPI.get();
    publishedFlows.value = (response.data || []).filter(
      flow => flow.status === 'published'
    );
  } catch {
    publishedFlows.value = [];
  }
};

const saveFlow = async status => {
  if (!flowName.value.trim()) {
    useAlert(t('FLOW_BUILDER.VALIDATION.NAME_REQUIRED'));
    return;
  }

  if (!selectedInboxId.value) {
    useAlert(t('FLOW_BUILDER.VALIDATION.INBOX_REQUIRED'));
    return;
  }

  const serializedFlow = flowBuilder.value?.serialize() || {};
  const validationErrors = getFlowValidationErrors(serializedFlow);

  if (validationErrors.length) {
    useAlert(t('FLOW_BUILDER.VALIDATION.INTERACTIVE_OPTION_TOO_LONG'));
    return;
  }

  isSaving.value = true;

  try {
    const payload = {
      ...buildPayload(status),
      flow_json: serializedFlow,
    };
    const response = flowId.value
      ? await botFlowsAPI.update(flowId.value, payload)
      : await botFlowsAPI.create(payload);

    currentStatus.value = response.data.status;

    if (!flowId.value) {
      flowId.value = response.data.id;
      router.replace({
        name: 'flow_builder_edit',
        params: { id: response.data.id },
      });
    }

    useAlert(
      status === 'published'
        ? t('FLOW_BUILDER.PUBLISH_SUCCESS')
        : t('FLOW_BUILDER.SAVE_SUCCESS')
    );
  } catch (error) {
    const apiErrorMessage = getApiErrorMessage(error);

    useAlert(
      apiErrorMessage ||
        (status === 'published'
          ? t('FLOW_BUILDER.PUBLISH_ERROR')
          : t('FLOW_BUILDER.SAVE_ERROR'))
    );
  } finally {
    isSaving.value = false;
  }
};

const addNode = async type => {
  await flowBuilder.value?.addNodeConnectedToSelection(type);
};

const runAutoLayout = async () => {
  await flowBuilder.value?.arrangeNodes();
};

const updateSelectedNode = async patch => {
  if (!flowBuilder.value || !selectedNode.value) return;
  await flowBuilder.value.updateNodeData(selectedNode.value.id, patch);

  if (quickEdit.value?.node?.id === selectedNode.value.id) {
    quickEdit.value = {
      ...quickEdit.value,
      node: flowBuilder.value.getNode(selectedNode.value.id),
    };
  }
};

async function updateStartTriggerMode(valueOrEvent) {
  const triggerMode = getOptionValue(valueOrEvent);
  await updateSelectedNode({
    triggerMode,
    matchType:
      triggerMode === 'keyword'
        ? selectedNode.value?.data?.matchType || 'exact'
        : 'exact',
    keyword:
      triggerMode === 'keyword' ? selectedNode.value?.data?.keyword || '' : '',
  });
}

async function updateStartMatchType(valueOrEvent) {
  const matchType = getOptionValue(valueOrEvent) || 'exact';
  await updateSelectedNode({ matchType });
}

const syncTemplatesForInbox = async () => {
  if (!selectedInboxId.value) {
    useAlert(t('FLOW_BUILDER.TEMPLATE.INBOX_REQUIRED'));
    return;
  }

  isSyncingTemplates.value = true;

  try {
    await InboxesAPI.syncTemplates(selectedInboxId.value);
    await store.dispatch('inboxes/get');
    useAlert(t('FLOW_BUILDER.TEMPLATE.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      getApiErrorMessage(error) || t('FLOW_BUILDER.TEMPLATE.SYNC_ERROR')
    );
  } finally {
    isSyncingTemplates.value = false;
  }
};

async function updateTemplateNode(valueOrEvent) {
  const templateId = getOptionValue(valueOrEvent);
  const template = selectedInboxTemplates.value.find(
    item => String(item.id) === String(templateId)
  );

  await updateSelectedNode({
    templateId: template ? String(template.id) : '',
    templateName: template?.name || '',
    templateLanguage: template?.language || '',
    templateCategory: template?.category || '',
    templateData: template || null,
    templateParams: {},
    renderedTemplate: '',
    body: template?.name || '',
  });
}

const selectedInboxModel = computed({
  get: () => selectedInboxId.value || '',
  set: value => {
    selectedInboxId.value = value ? String(value) : '';
  },
});
const selectedNodeTriggerModeModel = computed({
  get: () => selectedNode.value?.data?.triggerMode || 'no_match',
  set: value => {
    updateStartTriggerMode(value);
  },
});
const selectedNodeTriggerMatchTypeModel = computed({
  get: () => selectedNode.value?.data?.matchType || 'exact',
  set: value => {
    updateStartMatchType(value);
  },
});
const selectedInteractiveHeaderTypeModel = computed({
  get: () => selectedNode.value?.data?.headerType || 'none',
  set: value => {
    updateSelectedNode({
      headerType: value || 'none',
      headerText:
        (value || 'none') === 'text'
          ? selectedNode.value?.data?.headerText || ''
          : '',
    });
  },
});
const selectedButtonGenerationMethodModel = computed({
  get: () => selectedNode.value?.data?.buttonGenerationMethod || 'static',
  set: value => {
    updateSelectedNode({
      buttonGenerationMethod: value || 'static',
    });
  },
});
const selectedButtonActionTypeModel = computed({
  get: () => selectedNode.value?.data?.actionType || 'start_flow',
  set: value => {
    updateSelectedNode({
      actionType: value || 'start_flow',
      targetFlowId:
        (value || 'start_flow') === 'start_flow'
          ? selectedNode.value?.data?.targetFlowId || ''
          : '',
    });
  },
});
const selectedButtonTargetFlowModel = computed({
  get: () => selectedNode.value?.data?.targetFlowId || '',
  set: value => {
    const targetFlow = publishedFlows.value.find(
      flow => String(flow.id) === String(value)
    );

    updateSelectedNode({
      targetFlowId: value ? String(value) : '',
      targetFlowName: targetFlow?.name || '',
    });
  },
});
const buttonLabelsModel = computed({
  get: () => selectedNode.value?.data?.labels || [],
  set: value => {
    updateSelectedNode({ labels: value });
  },
});
const buttonAssignTeamModel = computed({
  get: () => selectedNode.value?.data?.assignTeamId || '',
  set: value => {
    updateSelectedNode({ assignTeamId: value ? String(value) : '' });
  },
});
const buttonAssignUserModel = computed({
  get: () => selectedNode.value?.data?.assignUserId || '',
  set: value => {
    updateSelectedNode({ assignUserId: value ? String(value) : '' });
  },
});
const selectedTemplateIdModel = computed({
  get: () => selectedNode.value?.data?.templateId || '',
  set: value => {
    updateTemplateNode(value);
  },
});
const quickEditTriggerModeModel = computed({
  get: () => quickEditNode.value?.data?.triggerMode || 'no_match',
  set: value => {
    updateStartTriggerMode(value);
  },
});
const quickEditTriggerMatchTypeModel = computed({
  get: () => quickEditNode.value?.data?.matchType || 'exact',
  set: value => {
    updateStartMatchType(value);
  },
});
const startLabelsModel = computed({
  get: () => selectedNode.value?.data?.labels || [],
  set: value => {
    updateSelectedNode({ labels: value });
  },
});
const startAssignTeamModel = computed({
  get: () => selectedNode.value?.data?.assignTeamId || '',
  set: value => {
    updateSelectedNode({ assignTeamId: value ? String(value) : '' });
  },
});
const startAssignUserModel = computed({
  get: () => selectedNode.value?.data?.assignUserId || '',
  set: value => {
    updateSelectedNode({ assignUserId: value ? String(value) : '' });
  },
});
const quickEditStartLabelsModel = computed({
  get: () => quickEditNode.value?.data?.labels || [],
  set: value => {
    updateSelectedNode({ labels: value });
  },
});
const quickEditStartAssignTeamModel = computed({
  get: () => quickEditNode.value?.data?.assignTeamId || '',
  set: value => {
    updateSelectedNode({ assignTeamId: value ? String(value) : '' });
  },
});
const quickEditStartAssignUserModel = computed({
  get: () => quickEditNode.value?.data?.assignUserId || '',
  set: value => {
    updateSelectedNode({ assignUserId: value ? String(value) : '' });
  },
});
const quickEditInteractiveHeaderTypeModel = computed({
  get: () => quickEditNode.value?.data?.headerType || 'none',
  set: value => {
    updateSelectedNode({
      headerType: value || 'none',
      headerText:
        (value || 'none') === 'text'
          ? quickEditNode.value?.data?.headerText || ''
          : '',
    });
  },
});
const quickEditButtonGenerationMethodModel = computed({
  get: () => quickEditNode.value?.data?.buttonGenerationMethod || 'static',
  set: value => {
    updateSelectedNode({
      buttonGenerationMethod: value || 'static',
    });
  },
});
const quickEditButtonActionTypeModel = computed({
  get: () => quickEditNode.value?.data?.actionType || 'start_flow',
  set: value => {
    updateSelectedNode({
      actionType: value || 'start_flow',
      targetFlowId:
        (value || 'start_flow') === 'start_flow'
          ? quickEditNode.value?.data?.targetFlowId || ''
          : '',
    });
  },
});
const quickEditButtonTargetFlowModel = computed({
  get: () => quickEditNode.value?.data?.targetFlowId || '',
  set: value => {
    const targetFlow = publishedFlows.value.find(
      flow => String(flow.id) === String(value)
    );

    updateSelectedNode({
      targetFlowId: value ? String(value) : '',
      targetFlowName: targetFlow?.name || '',
    });
  },
});
const quickEditButtonLabelsModel = computed({
  get: () => quickEditNode.value?.data?.labels || [],
  set: value => {
    updateSelectedNode({ labels: value });
  },
});
const quickEditButtonAssignTeamModel = computed({
  get: () => quickEditNode.value?.data?.assignTeamId || '',
  set: value => {
    updateSelectedNode({ assignTeamId: value ? String(value) : '' });
  },
});
const quickEditButtonAssignUserModel = computed({
  get: () => quickEditNode.value?.data?.assignUserId || '',
  set: value => {
    updateSelectedNode({ assignUserId: value ? String(value) : '' });
  },
});

const selectedButtonTitleError = computed(() =>
  selectedNodeType.value === 'button' &&
  exceedsInteractiveOptionLength(selectedNode.value?.data?.buttonTitle)
    ? t('FLOW_BUILDER.VALIDATION.BUTTON_TITLE_MAX_20')
    : ''
);

const selectedRowTitleError = computed(() =>
  selectedNodeType.value === 'row' &&
  exceedsInteractiveOptionLength(selectedNode.value?.data?.rowTitle)
    ? t('FLOW_BUILDER.VALIDATION.ROW_TITLE_MAX_20')
    : ''
);

const quickEditButtonTitleError = computed(() =>
  quickEditNodeType.value === 'button' &&
  exceedsInteractiveOptionLength(quickEditNode.value?.data?.buttonTitle)
    ? t('FLOW_BUILDER.VALIDATION.BUTTON_TITLE_MAX_20')
    : ''
);

const quickEditRowTitleError = computed(() =>
  quickEditNodeType.value === 'row' &&
  exceedsInteractiveOptionLength(quickEditNode.value?.data?.rowTitle)
    ? t('FLOW_BUILDER.VALIDATION.ROW_TITLE_MAX_20')
    : ''
);

const syncSelectedTemplateParserState = async () => {
  if (
    selectedNodeType.value !== 'template' ||
    !selectedTemplateDefinition.value
  ) {
    return;
  }

  const parser = templateParserRef.value;
  const processedParams = parser?.processedParams?.value || {};
  const renderedTemplate = parser?.renderedTemplate?.value || '';
  const currentParams = selectedNode.value?.data?.templateParams || {};
  const currentRenderedTemplate =
    selectedNode.value?.data?.renderedTemplate || '';

  if (
    JSON.stringify(processedParams) === JSON.stringify(currentParams) &&
    renderedTemplate === currentRenderedTemplate
  ) {
    return;
  }

  await updateSelectedNode({
    templateParams: processedParams,
    renderedTemplate,
  });
};

watch(
  () => [
    selectedNode.value?.id,
    selectedNode.value?.data?.templateId,
    selectedInboxId.value,
  ],
  () => {
    templateParserRef.value = null;
  }
);

watch(
  () => templateParserRef.value?.processedParams?.value,
  () => {
    syncSelectedTemplateParserState().catch(() => {});
  },
  { deep: true }
);

watch(
  () => templateParserRef.value?.renderedTemplate?.value,
  () => {
    syncSelectedTemplateParserState().catch(() => {});
  }
);

const deleteSelectedNode = async () => {
  const removed = await flowBuilder.value?.removeSelectedNode();
  contextMenu.value = null;

  if (removed) {
    if (quickEdit.value?.node?.id === selectedNode.value?.id) {
      closeQuickEdit();
    }
    useAlert(t('FLOW_BUILDER.DELETE_NODE_SUCCESS'));
  }
};

const selectNodeById = async nodeId => {
  if (!nodeId || !flowBuilder.value) return;
  await flowBuilder.value.selectNode(nodeId);
};

const onStageContextMenu = async event => {
  const nodeElement = event.target.closest('.flow-node[data-node-id]');

  if (!nodeElement) return;

  event.preventDefault();
  event.stopPropagation();

  const nodeId = nodeElement.dataset.nodeId;

  await selectNodeById(nodeId);

  contextMenu.value = {
    x: event.clientX,
    y: event.clientY,
    node: flowBuilder.value.getNode(nodeId),
  };
};

const onStageDoubleClick = async event => {
  const nodeElement = event.target.closest('.flow-node[data-node-id]');

  if (!nodeElement) return;

  const nodeId = nodeElement.dataset.nodeId;

  await selectNodeById(nodeId);
  openQuickEdit(flowBuilder.value.getNode(nodeId));
};

const handleContextMenuAction = ({ action }) => {
  if (action === 'delete') {
    deleteSelectedNode();
  }
};

const clearDraftConnection = () => {
  draftConnection.value = null;
};

const onSocketPointerDown = event => {
  const socketElement = event.target.closest('[data-flow-socket]');

  if (!socketElement) return;
  if (socketElement.dataset.socketSide !== 'output') return;

  event.stopPropagation();

  const socket = {
    nodeId: socketElement.dataset.nodeId,
    key: socketElement.dataset.socketKey,
    side: socketElement.dataset.socketSide,
  };

  draftConnection.value = {
    socket,
    start: getSocketCenter(socketElement),
    end: getSocketCenter(socketElement),
  };
};

const onPointerMove = event => {
  if (!draftConnection.value) return;

  draftConnection.value = {
    ...draftConnection.value,
    end: getPointFromClientPosition(event.clientX, event.clientY),
  };
};

const onPointerUp = async event => {
  if (!draftConnection.value || !flowBuilder.value) return;

  const source = draftConnection.value.socket;
  const targetElement = event.target.closest('[data-flow-socket]');

  if (targetElement) {
    if (targetElement.dataset.socketSide !== 'input') {
      clearDraftConnection();
      return;
    }

    const target = {
      nodeId: targetElement.dataset.nodeId,
      key: targetElement.dataset.socketKey,
      side: targetElement.dataset.socketSide,
    };

    if (source.side !== target.side && source.nodeId !== target.nodeId) {
      const normalized =
        source.side === 'output'
          ? {
              sourceNodeId: source.nodeId,
              sourceOutput: source.key,
              targetNodeId: target.nodeId,
              targetInput: target.key,
            }
          : {
              sourceNodeId: target.nodeId,
              sourceOutput: target.key,
              targetNodeId: source.nodeId,
              targetInput: source.key,
            };

      await flowBuilder.value.connectSockets(normalized);
    }
  } else {
    await flowBuilder.value.spawnNodeFromOutput({
      sourceNodeId: source.nodeId,
      sourceOutput: source.key,
      position: {
        x: draftConnection.value.end.x - 180,
        y: draftConnection.value.end.y - 120,
      },
    });
  }

  clearDraftConnection();
};

onMounted(async () => {
  store.dispatch('inboxes/get');
  store.dispatch('labels/get');
  store.dispatch('teams/get');
  store.dispatch('agents/get');
  fetchPublishedFlows();

  flowBuilder.value = await createReteFlowBuilder(editorContainerRef.value, {
    onContextMenu: updateContextMenu,
    onSelectionChange: node => {
      selectedNode.value = node;

      if (quickEdit.value?.node?.id === node?.id) {
        quickEdit.value = {
          ...quickEdit.value,
          node,
        };
        syncQuickEditPosition();
      }
    },
  });

  if (flowId.value) {
    await loadFlow(flowId.value);
  }

  window.addEventListener('click', handleCanvasClick);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', handleCanvasClick);
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
  flowBuilder.value?.destroy();
});
</script>

<template>
  <div
    dir="ltr"
    class="flow-builder-shell flex h-full w-full flex-col bg-[#f8fbff] font-inter"
  >
    <header
      class="flex flex-wrap items-center justify-between gap-3 border-b border-[#dbe8f6] bg-white px-4 py-3"
    >
      <div class="flex min-w-[260px] flex-1 flex-col gap-1.5">
        <Button
          icon="i-lucide-chevron-left"
          color="slate"
          variant="ghost"
          class="w-fit !px-0 !text-sm"
          :label="$t('FLOW_BUILDER.BACK')"
          @click="router.push({ name: 'flow_builder_index' })"
        />
        <div>
          <h1 class="text-lg font-semibold text-slate-900">{{ pageTitle }}</h1>
          <p class="text-xs text-slate-500">
            {{ $t('FLOW_BUILDER.EDITOR_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="grid min-w-[260px] flex-1 gap-2 sm:grid-cols-2">
        <Input
          v-model="flowName"
          :placeholder="$t('FLOW_BUILDER.FORM.NAME_PLACEHOLDER')"
        />
        <ComboBox
          v-model="selectedInboxModel"
          :options="whatsappInboxOptions"
          :placeholder="$t('FLOW_BUILDER.FORM.SELECT_INBOX')"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>

      <div class="flex items-center gap-2 self-start sm:self-auto">
        <span
          class="rounded-full px-3 py-1 text-xs font-medium"
          :class="
            currentStatus === 'published'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-amber-50 text-amber-700'
          "
        >
          {{ currentStatus }}
        </span>
        <Button
          icon="i-lucide-workflow"
          ghost
          slate
          sm
          :title="$t('FLOW_BUILDER.AUTO_LAYOUT')"
          class="flow-toolbar__item"
          @click="runAutoLayout"
        />
        <Button
          icon="i-lucide-save"
          :label="$t('FLOW_BUILDER.SAVE')"
          slate
          :is-loading="isSaving"
          @click="saveFlow('draft')"
        />
        <Button
          icon="i-lucide-send"
          :label="$t('FLOW_BUILDER.PUBLISH')"
          :is-loading="isSaving"
          @click="saveFlow('published')"
        />
      </div>
    </header>

    <div class="flow-toolbar">
      <Button
        v-for="item in toolbarNodes"
        :key="item.type"
        ghost
        slate
        sm
        :title="item.label"
        :icon="item.icon"
        class="flow-toolbar__item"
        :class="{ 'flow-toolbar__item--active': item.active }"
        @click="addNode(item.type)"
      />
    </div>

    <div class="grid min-h-0 flex-1 grid-cols-[340px_minmax(0,1fr)]">
      <aside class="flow-inspector">
        <template v-if="selectedNode">
          <div class="flow-inspector__header">
            <h2 class="flow-inspector__title">{{ selectedNode.label }}</h2>
            <span class="flow-inspector__type">{{ selectedNodeType }}</span>
          </div>

          <div
            v-if="selectedNodeType === 'start'"
            class="flow-inspector__group"
          >
            <ComboBox
              v-model="selectedNodeTriggerModeModel"
              :options="triggerModeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-if="selectedNode.data.triggerMode === 'keyword'"
              v-model="selectedNodeTriggerMatchTypeModel"
              :options="triggerMatchTypeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.KEYWORD')"
              :disabled="selectedNode.data.triggerMode !== 'keyword'"
              :model-value="selectedNode.data.keyword"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.KEYWORD_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ keyword: $event })"
            />

            <TagMultiSelectComboBox
              v-model="startLabelsModel"
              :options="labelOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.LABELS_PLACEHOLDER')"
              class="[&>div]:bg-n-alpha-black2"
            />

            <ComboBox
              v-model="startAssignTeamModel"
              :options="teamOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-model="startAssignUserModel"
              :options="agentOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              :model-value="selectedNode.data.webhookUrl || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              @update:model-value="updateSelectedNode({ webhookUrl: $event })"
            />
          </div>

          <div
            v-else-if="selectedNodeType === 'text'"
            class="flow-inspector__group"
          >
            <TextArea
              :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
              :model-value="selectedNode.data.body || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ body: $event })"
            />

            <div class="flex items-center justify-between gap-3">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('FLOW_BUILDER.INSPECTOR.TYPING_ON_DISPLAY') }}
              </span>
              <Switch
                :model-value="Boolean(selectedNode.data.typingOnDisplay)"
                @update:model-value="
                  updateSelectedNode({ typingOnDisplay: $event })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_SECONDS') }}</span>
                <span>{{ selectedNode.data.delaySeconds || 0 }}</span>
              </label>
              <input
                :value="selectedNode.data.delaySeconds || 0"
                type="range"
                min="0"
                max="59"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delaySeconds: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_MINUTES') }}</span>
                <span>{{ selectedNode.data.delayMinutes || 0 }}</span>
              </label>
              <input
                :value="selectedNode.data.delayMinutes || 0"
                type="range"
                min="0"
                max="59"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delayMinutes: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_HOURS') }}</span>
                <span>{{ selectedNode.data.delayHours || 0 }}</span>
              </label>
              <input
                :value="selectedNode.data.delayHours || 0"
                type="range"
                min="0"
                max="23"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delayHours: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>
          </div>

          <div
            v-else-if="selectedNodeType === 'interactive'"
            class="flow-inspector__group"
          >
            <ComboBox
              v-model="selectedInteractiveHeaderTypeModel"
              :options="interactiveHeaderTypeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              v-if="selectedNode.data.headerType === 'text'"
              :label="$t('FLOW_BUILDER.INSPECTOR.HEADER_TEXT')"
              :model-value="selectedNode.data.headerText || ''"
              :placeholder="
                $t('FLOW_BUILDER.INSPECTOR.HEADER_TEXT_PLACEHOLDER')
              "
              @update:model-value="updateSelectedNode({ headerText: $event })"
            />

            <TextArea
              :label="$t('FLOW_BUILDER.INSPECTOR.BODY')"
              :model-value="selectedNode.data.body"
              :placeholder="
                $t('FLOW_BUILDER.INSPECTOR.INTERACTIVE_PLACEHOLDER')
              "
              @update:model-value="updateSelectedNode({ body: $event })"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.FOOTER')"
              :model-value="selectedNode.data.footer"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.FOOTER_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ footer: $event })"
            />
          </div>

          <div
            v-else-if="selectedNodeType === 'button'"
            class="flow-inspector__group"
          >
            <ComboBox
              v-model="selectedButtonGenerationMethodModel"
              :options="buttonGenerationMethodOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.BUTTON_TITLE')"
              :model-value="selectedNode.data.buttonTitle"
              :message="selectedButtonTitleError"
              :message-type="selectedButtonTitleError ? 'error' : 'info'"
              @update:model-value="updateSelectedNode({ buttonTitle: $event })"
            />

            <ComboBox
              v-model="selectedButtonActionTypeModel"
              :options="buttonActionOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-if="selectedNode.data.actionType === 'start_flow'"
              v-model="selectedButtonTargetFlowModel"
              :options="publishedFlowOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.FLOW_NAME')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <template v-else>
              <TextArea
                :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
                :model-value="selectedNode.data.body || ''"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_PLACEHOLDER')"
                @update:model-value="updateSelectedNode({ body: $event })"
              />

              <TagMultiSelectComboBox
                v-model="buttonLabelsModel"
                :options="labelOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.LABELS_PLACEHOLDER')"
                class="[&>div]:bg-n-alpha-black2"
              />

              <ComboBox
                v-model="buttonAssignTeamModel"
                :options="teamOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              />

              <ComboBox
                v-model="buttonAssignUserModel"
                :options="agentOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              />

              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                :model-value="selectedNode.data.webhookUrl || ''"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                @update:model-value="updateSelectedNode({ webhookUrl: $event })"
              />
            </template>
          </div>

          <div
            v-else-if="selectedNodeType === 'list_message'"
            class="flow-inspector__group"
          >
            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.LIST_BUTTON_LABEL')"
              :model-value="selectedNode.data.buttonLabel"
              @update:model-value="updateSelectedNode({ buttonLabel: $event })"
            />
          </div>

          <div
            v-else-if="selectedNodeType === 'section'"
            class="flow-inspector__group"
          >
            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.SECTION_TITLE')"
              :model-value="selectedNode.data.sectionTitle"
              @update:model-value="updateSelectedNode({ sectionTitle: $event })"
            />
          </div>

          <div
            v-else-if="selectedNodeType === 'row'"
            class="flow-inspector__group"
          >
            <ComboBox
              :model-value="selectedNode.data.rowGenerationMethod || 'static'"
              :options="rowGenerationMethodOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              @update:model-value="
                updateSelectedNode({ rowGenerationMethod: $event || 'static' })
              "
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.ROW_TITLE')"
              :model-value="selectedNode.data.rowTitle"
              :message="selectedRowTitleError"
              :message-type="selectedRowTitleError ? 'error' : 'info'"
              @update:model-value="updateSelectedNode({ rowTitle: $event })"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.ROW_DESCRIPTION')"
              :model-value="selectedNode.data.description"
              @update:model-value="updateSelectedNode({ description: $event })"
            />

            <TagMultiSelectComboBox
              :model-value="selectedNode.data.addLabels || []"
              :options="labelOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ADD_LABELS')"
              class="[&>div]:bg-n-alpha-black2"
              @update:model-value="updateSelectedNode({ addLabels: $event })"
            />

            <TagMultiSelectComboBox
              :model-value="selectedNode.data.removeLabels || []"
              :options="labelOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.REMOVE_LABELS')"
              class="[&>div]:bg-n-alpha-black2"
              @update:model-value="updateSelectedNode({ removeLabels: $event })"
            />

            <ComboBox
              :model-value="selectedNode.data.assignUserId || ''"
              :options="agentOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              @update:model-value="
                updateSelectedNode({
                  assignUserId: $event ? String($event) : '',
                })
              "
            />

            <ComboBox
              :model-value="selectedNode.data.assignTeamId || ''"
              :options="teamOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              @update:model-value="
                updateSelectedNode({
                  assignTeamId: $event ? String($event) : '',
                })
              "
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              :model-value="selectedNode.data.webhookUrl || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              @update:model-value="updateSelectedNode({ webhookUrl: $event })"
            />
          </div>

          <div v-else class="flow-inspector__group">
            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.TITLE')"
              :model-value="selectedNode.data.title"
              @update:model-value="updateSelectedNode({ title: $event })"
            />

            <TextArea
              :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
              :model-value="selectedNode.data.body"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ body: $event })"
            />
          </div>

          <div
            v-if="selectedNodeType === 'template'"
            class="flow-inspector__group"
          >
            <div class="flow-inspector__subheader">
              <span>{{ $t('FLOW_BUILDER.TEMPLATE.SECTION_TITLE') }}</span>
              <Button
                xs
                slate
                faded
                icon="i-lucide-refresh-cw"
                :is-loading="isSyncingTemplates"
                @click="syncTemplatesForInbox"
              />
            </div>

            <label class="flow-inspector__label">
              {{ $t('FLOW_BUILDER.TEMPLATE.SELECT_LABEL') }}
            </label>
            <ComboBox
              v-model="selectedTemplateIdModel"
              :options="templateOptions"
              :disabled="!selectedInboxId"
              :placeholder="$t('FLOW_BUILDER.TEMPLATE.SELECT_PLACEHOLDER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <p v-if="!selectedInboxId" class="flow-inspector__hint">
              {{ $t('FLOW_BUILDER.TEMPLATE.INBOX_REQUIRED') }}
            </p>
            <p
              v-else-if="selectedInboxId && !templateOptions.length"
              class="flow-inspector__hint"
            >
              {{ $t('FLOW_BUILDER.TEMPLATE.EMPTY') }}
            </p>

            <div v-if="selectedTemplateDefinition" class="flow-inspector__card">
              <div class="flow-inspector__template-meta">
                <span>{{ selectedTemplateDefinition.name }}</span>
                <span>
                  {{
                    selectedTemplateDefinition.language ||
                    selectedTemplateDefinition.category
                  }}
                </span>
              </div>

              <WhatsAppTemplateParser
                ref="templateParserRef"
                :template="selectedTemplateDefinition"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="flow-inspector__empty">
            {{ $t('FLOW_BUILDER.INSPECTOR.EMPTY') }}
          </div>
        </template>
      </aside>

      <section
        ref="editorStageRef"
        class="relative min-h-0 overflow-hidden border-l border-[#dbe8f6]"
        @contextmenu="onStageContextMenu"
        @dblclick="onStageDoubleClick"
        @pointerdown.capture="onSocketPointerDown"
      >
        <div
          v-if="isLoading"
          class="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
        >
          {{ $t('FLOW_BUILDER.LOADING_EDITOR') }}
        </div>

        <div ref="editorContainerRef" class="flow-editor" />

        <svg
          v-if="draftConnection"
          class="flow-draft-connection"
          width="100%"
          height="100%"
        >
          <path :d="buildDraftConnectionPath(draftConnection)" />
        </svg>

        <div
          v-if="contextMenu?.node"
          class="flow-context-menu"
          :style="{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px` }"
        >
          <DropdownMenu
            :menu-items="contextMenuItems"
            @action="handleContextMenuAction"
          />
        </div>

        <div
          v-if="quickEditNode"
          class="flow-quick-edit"
          :style="{ left: `${quickEdit.x}px`, top: `${quickEdit.y}px` }"
        >
          <div class="flow-quick-edit__header">
            <strong>{{ quickEditNode.label }}</strong>
            <Button
              ghost
              slate
              xs
              icon="i-lucide-x"
              class="flow-quick-edit__close"
              @click="closeQuickEdit"
            />
          </div>

          <div
            v-if="quickEditNodeType === 'start'"
            class="flow-quick-edit__body"
          >
            <ComboBox
              v-model="quickEditTriggerModeModel"
              :options="triggerModeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-if="quickEditNode.data.triggerMode === 'keyword'"
              v-model="quickEditTriggerMatchTypeModel"
              :options="triggerMatchTypeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.KEYWORD')"
              :disabled="quickEditNode.data.triggerMode !== 'keyword'"
              :model-value="quickEditNode.data.keyword"
              @update:model-value="updateSelectedNode({ keyword: $event })"
            />

            <TagMultiSelectComboBox
              v-model="quickEditStartLabelsModel"
              :options="labelOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.LABELS_PLACEHOLDER')"
              class="[&>div]:bg-n-alpha-black2"
            />

            <ComboBox
              v-model="quickEditStartAssignTeamModel"
              :options="teamOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-model="quickEditStartAssignUserModel"
              :options="agentOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              :model-value="quickEditNode.data.webhookUrl || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
              @update:model-value="updateSelectedNode({ webhookUrl: $event })"
            />
          </div>

          <div
            v-else-if="quickEditNodeType === 'text'"
            class="flow-quick-edit__body"
          >
            <TextArea
              :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
              :model-value="quickEditNode.data.body || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ body: $event })"
            />

            <div class="flex items-center justify-between gap-3">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('FLOW_BUILDER.INSPECTOR.TYPING_ON_DISPLAY') }}
              </span>
              <Switch
                :model-value="Boolean(quickEditNode.data.typingOnDisplay)"
                @update:model-value="
                  updateSelectedNode({ typingOnDisplay: $event })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_SECONDS') }}</span>
                <span>{{ quickEditNode.data.delaySeconds || 0 }}</span>
              </label>
              <input
                :value="quickEditNode.data.delaySeconds || 0"
                type="range"
                min="0"
                max="59"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delaySeconds: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_MINUTES') }}</span>
                <span>{{ quickEditNode.data.delayMinutes || 0 }}</span>
              </label>
              <input
                :value="quickEditNode.data.delayMinutes || 0"
                type="range"
                min="0"
                max="59"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delayMinutes: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>

            <div class="flex flex-col gap-2">
              <label
                class="flex items-center justify-between gap-3 text-sm font-medium text-n-slate-12"
              >
                <span>{{ $t('FLOW_BUILDER.INSPECTOR.DELAY_HOURS') }}</span>
                <span>{{ quickEditNode.data.delayHours || 0 }}</span>
              </label>
              <input
                :value="quickEditNode.data.delayHours || 0"
                type="range"
                min="0"
                max="23"
                step="1"
                class="w-full"
                @input="
                  updateSelectedNode({
                    delayHours: normalizeDelayValue($event.target.value),
                  })
                "
              />
            </div>
          </div>

          <div
            v-else-if="quickEditNodeType === 'button'"
            class="flow-quick-edit__body"
          >
            <ComboBox
              v-model="quickEditButtonGenerationMethodModel"
              :options="buttonGenerationMethodOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.BUTTON_TITLE')"
              :model-value="quickEditNode.data.buttonTitle"
              :message="quickEditButtonTitleError"
              :message-type="quickEditButtonTitleError ? 'error' : 'info'"
              @update:model-value="updateSelectedNode({ buttonTitle: $event })"
            />

            <ComboBox
              v-model="quickEditButtonActionTypeModel"
              :options="buttonActionOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <ComboBox
              v-if="quickEditNode.data.actionType === 'start_flow'"
              v-model="quickEditButtonTargetFlowModel"
              :options="publishedFlowOptions"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.FLOW_NAME')"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <template v-else>
              <TextArea
                :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
                :model-value="quickEditNode.data.body || ''"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_PLACEHOLDER')"
                @update:model-value="updateSelectedNode({ body: $event })"
              />

              <TagMultiSelectComboBox
                v-model="quickEditButtonLabelsModel"
                :options="labelOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.LABELS_PLACEHOLDER')"
                class="[&>div]:bg-n-alpha-black2"
              />

              <ComboBox
                v-model="quickEditButtonAssignTeamModel"
                :options="teamOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              />

              <ComboBox
                v-model="quickEditButtonAssignUserModel"
                :options="agentOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
              />

              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                :model-value="quickEditNode.data.webhookUrl || ''"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                @update:model-value="updateSelectedNode({ webhookUrl: $event })"
              />
            </template>
          </div>

          <div
            v-else-if="quickEditNodeType === 'interactive'"
            class="flow-quick-edit__body"
          >
            <ComboBox
              v-model="quickEditInteractiveHeaderTypeModel"
              :options="interactiveHeaderTypeOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
            />

            <Input
              v-if="quickEditNode.data.headerType === 'text'"
              :label="$t('FLOW_BUILDER.INSPECTOR.HEADER_TEXT')"
              :model-value="quickEditNode.data.headerText || ''"
              :placeholder="
                $t('FLOW_BUILDER.INSPECTOR.HEADER_TEXT_PLACEHOLDER')
              "
              @update:model-value="updateSelectedNode({ headerText: $event })"
            />

            <TextArea
              :label="$t('FLOW_BUILDER.INSPECTOR.BODY')"
              :model-value="quickEditNode.data.body"
              @update:model-value="updateSelectedNode({ body: $event })"
            />

            <Input
              :label="$t('FLOW_BUILDER.INSPECTOR.FOOTER')"
              :model-value="quickEditNode.data.footer || ''"
              :placeholder="$t('FLOW_BUILDER.INSPECTOR.FOOTER_PLACEHOLDER')"
              @update:model-value="updateSelectedNode({ footer: $event })"
            />
          </div>

          <div v-else class="flow-quick-edit__body">
            <template v-if="quickEditNodeType === 'list_message'">
              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.LIST_BUTTON_LABEL')"
                :model-value="quickEditNode.data.buttonLabel"
                @update:model-value="
                  updateSelectedNode({ buttonLabel: $event })
                "
              />
            </template>

            <template v-else-if="quickEditNodeType === 'section'">
              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.SECTION_TITLE')"
                :model-value="quickEditNode.data.sectionTitle"
                @update:model-value="
                  updateSelectedNode({ sectionTitle: $event })
                "
              />
            </template>

            <template v-else-if="quickEditNodeType === 'row'">
              <ComboBox
                :model-value="
                  quickEditNode.data.rowGenerationMethod || 'static'
                "
                :options="rowGenerationMethodOptions"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
                @update:model-value="
                  updateSelectedNode({
                    rowGenerationMethod: $event || 'static',
                  })
                "
              />

              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.ROW_TITLE')"
                :model-value="quickEditNode.data.rowTitle"
                :message="quickEditRowTitleError"
                :message-type="quickEditRowTitleError ? 'error' : 'info'"
                @update:model-value="updateSelectedNode({ rowTitle: $event })"
              />

              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.ROW_DESCRIPTION')"
                :model-value="quickEditNode.data.description"
                @update:model-value="
                  updateSelectedNode({ description: $event })
                "
              />

              <TagMultiSelectComboBox
                :model-value="quickEditNode.data.addLabels || []"
                :options="labelOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ADD_LABELS')"
                class="[&>div]:bg-n-alpha-black2"
                @update:model-value="updateSelectedNode({ addLabels: $event })"
              />

              <TagMultiSelectComboBox
                :model-value="quickEditNode.data.removeLabels || []"
                :options="labelOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.REMOVE_LABELS')"
                class="[&>div]:bg-n-alpha-black2"
                @update:model-value="
                  updateSelectedNode({ removeLabels: $event })
                "
              />

              <ComboBox
                :model-value="quickEditNode.data.assignUserId || ''"
                :options="agentOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_USER')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
                @update:model-value="
                  updateSelectedNode({
                    assignUserId: $event ? String($event) : '',
                  })
                "
              />

              <ComboBox
                :model-value="quickEditNode.data.assignTeamId || ''"
                :options="teamOptions"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.ASSIGN_TEAM')"
                class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
                @update:model-value="
                  updateSelectedNode({
                    assignTeamId: $event ? String($event) : '',
                  })
                "
              />

              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                :model-value="quickEditNode.data.webhookUrl || ''"
                :placeholder="$t('FLOW_BUILDER.INSPECTOR.WEBHOOK_URL')"
                @update:model-value="updateSelectedNode({ webhookUrl: $event })"
              />
            </template>

            <template v-else>
              <Input
                :label="$t('FLOW_BUILDER.INSPECTOR.TITLE')"
                :model-value="quickEditNode.data.title"
                @update:model-value="updateSelectedNode({ title: $event })"
              />

              <TextArea
                :label="$t('FLOW_BUILDER.INSPECTOR.MESSAGE_BODY')"
                :model-value="quickEditNode.data.body"
                @update:model-value="updateSelectedNode({ body: $event })"
              />
            </template>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.flow-builder-shell,
.flow-builder-shell * {
  direction: ltr;
}

.flow-toolbar {
  display: flex;
  gap: 0.9rem;
  overflow-x: auto;
  border-bottom: 1px solid #dbe8f6;
  background: linear-gradient(180deg, #fdfefe 0%, #f6faff 100%);
  padding: 0.65rem 1rem;
}

.flow-toolbar__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.55rem;
  height: 2.55rem;
  flex: 0 0 auto;
  border: 1px dashed #c8d7e9;
  border-radius: 999px;
  background: #fff;
  color: #0b4f8d;
  font-size: 1.05rem;
  transition:
    transform 140ms ease,
    border-color 140ms ease,
    box-shadow 140ms ease;
}

.flow-toolbar__item:hover {
  transform: translateY(-1px);
  border-color: #8fc0ff;
  box-shadow: 0 10px 18px rgba(59, 130, 246, 0.12);
}

.flow-toolbar__item--active {
  border-color: #1d8cff;
  box-shadow: inset 0 0 0 1px rgba(29, 140, 255, 0.14);
}

.flow-editor {
  height: 100%;
  min-height: 760px;
  background: radial-gradient(
      circle at top,
      rgba(52, 152, 219, 0.06),
      transparent 24%
    ),
    linear-gradient(0deg, rgba(148, 163, 184, 0.12) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.12) 1px, transparent 1px),
    #f8fbff;
  background-size:
    auto,
    32px 32px,
    32px 32px,
    auto;
}

.flow-editor:deep(svg path) {
  stroke: #7f93a8;
  stroke-width: 3px;
}

.flow-draft-connection {
  position: absolute;
  inset: 0;
  z-index: 80;
  pointer-events: none;
}

.flow-draft-connection path {
  fill: none;
  stroke: #4f84b9;
  stroke-width: 4px;
  stroke-linecap: round;
}

.flow-context-menu {
  position: fixed;
  z-index: 30;
  min-width: 140px;
}

.flow-inspector__subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.5rem;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
}

.flow-inspector__subheader--nested {
  margin-top: 1rem;
}

.flow-inspector__card,
.flow-inspector__nested-card {
  border: 1px solid #d9e5f2;
  border-radius: 16px;
  background: #fff;
  padding: 0.9rem;
}

.flow-inspector__nested-card {
  margin-top: 0.75rem;
  background: #f8fbff;
}

.flow-inspector__card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.flow-inspector__card-title {
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 700;
}

.flow-inspector {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;
  overflow: auto;
  background: #fff;
  padding: 1.4rem;
  text-align: left;
}

.flow-inspector__header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 1rem;
}

.flow-inspector__title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.flow-inspector__type {
  color: #64748b;
  font-size: 0.82rem;
  text-transform: capitalize;
}

.flow-inspector__group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.flow-inspector__label {
  font-size: 0.83rem;
  font-weight: 600;
  color: #334155;
}

.flow-inspector__empty {
  margin-top: 2rem;
  border: 1px dashed #d8e2ef;
  border-radius: 18px;
  padding: 1.3rem;
  color: #64748b;
  text-align: center;
}

.flow-inspector__hint {
  margin: 0;
  color: #64748b;
  font-size: 0.82rem;
}

.flow-inspector__template-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
  color: #334155;
  font-size: 0.82rem;
  font-weight: 600;
}

.flow-quick-edit {
  position: absolute;
  z-index: 35;
  width: 320px;
  max-width: calc(100% - 48px);
  border: 1px solid #d8e2ef;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(8px);
  text-align: left;
}

.flow-quick-edit__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.flow-quick-edit__body {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;
}

.flow-quick-edit__close {
  border: 0;
  background: transparent;
  color: #64748b;
  font-size: 1.25rem;
  line-height: 1;
}
</style>
